/**
 * PromptEditor — tab for managing the prompt library.
 * Lists all prompts with an edit form for the selected one.
 */
import { DEFAULT_SCALE } from '../stores/PromptStore.js';
import { withFeedback } from '../lib/buttonFeedback.js';

const SCALE_RANGES = ['1-2', '3-4', '5-6', '7-8', '9-10'];

export class PromptEditor {
    constructor(options = {}) {
        this._promptStore = options.promptStore;
        this._userEmail = options.userEmail || '';
        this._statusBar = options.statusBar;
        this._onInvalidateScores = options.onInvalidateScores || (() => {});
        this._onAddToProject = options.onAddToProject || (() => {});
        this._selectedId = null;
        this._el = document.createElement('div');
        this._el.className = 'rm-prompt-editor';
    }

    get element() { return this._el; }

    setPrompts(prompts) {
        this._prompts = prompts || [];
        this.render();
    }

    render() {
        this._el.innerHTML = '';

        // Top bar
        const topBar = document.createElement('div');
        topBar.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
        topBar.innerHTML = `<h3 style="margin:0;color:var(--text);">Evaluation Criteria Library</h3>`;
        const addBtn = document.createElement('button');
        addBtn.className = 'rm-btn rm-btn-primary';
        addBtn.textContent = '+ New Criterion';
        addBtn.addEventListener('click', () => this._createNew());
        topBar.appendChild(addBtn);
        this._el.appendChild(topBar);

        // Split layout
        const split = document.createElement('div');
        split.style.cssText = 'display:flex;gap:16px;flex:1;min-height:0;';

        // Left: list
        const listPanel = document.createElement('div');
        listPanel.style.cssText = 'width:280px;flex-shrink:0;overflow-y:auto;display:flex;flex-direction:column;gap:8px;';

        for (const p of this._prompts) {
            const card = document.createElement('div');
            card.className = `rm-prompt-card${p.id === this._selectedId ? ' active' : ''}`;
            card.innerHTML = `
                <div class="rm-prompt-card-header">
                    <span class="rm-prompt-card-name">${this._esc(p.name)}</span>
                </div>
                <div class="rm-prompt-card-desc">${this._esc(p.description)}</div>
            `;
            card.addEventListener('click', () => { this._selectedId = p.id; this.render(); });
            listPanel.appendChild(card);
        }

        if (this._prompts.length === 0) {
            listPanel.innerHTML = '<div class="rm-empty-state" style="padding:20px;"><div>No criteria yet</div></div>';
        }

        split.appendChild(listPanel);

        // Right: edit form
        const formPanel = document.createElement('div');
        formPanel.style.cssText = 'flex:1;min-width:0;overflow-y:auto;';

        const selected = this._prompts.find(p => p.id === this._selectedId);
        if (selected) {
            formPanel.appendChild(this._buildForm(selected));
        } else {
            formPanel.innerHTML = `
                <div class="rm-empty-state">
                    <div>Select a criterion to edit, or create a new one</div>
                </div>`;
        }

        split.appendChild(formPanel);
        this._el.appendChild(split);
    }

    _buildForm(prompt) {
        const form = document.createElement('div');
        form.className = 'rm-prompt-form';

        const scale = prompt.scaleDescription || { ...DEFAULT_SCALE };

        form.innerHTML = `
            <div class="rm-form-row">
                <label>Name</label>
                <input type="text" id="rmPName" value="${this._esc(prompt.name)}">
            </div>
            <div class="rm-form-row">
                <label>Description</label>
                <input type="text" id="rmPDesc" value="${this._esc(prompt.description)}">
            </div>
            <div class="rm-form-row">
                <label>Evaluation Prompt</label>
                <textarea id="rmPText">${this._esc(prompt.promptText)}</textarea>
                <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">
                    This prompt will be sent to Claude along with the candidate's resume. It should ask for a 1-10 score.
                    The scale descriptions below are automatically appended.
                </div>
            </div>
            <div class="rm-form-row">
                <label>Scale Descriptions</label>
                <div class="rm-scale-grid">
                    ${SCALE_RANGES.map(r => `
                        <span class="rm-scale-label">${r}:</span>
                        <input class="rm-scale-input" data-range="${r}" value="${this._esc(scale[r] || '')}">
                    `).join('')}
                </div>
            </div>
            <div class="rm-form-row">
                <label style="display:flex;align-items:center;gap:8px;cursor:pointer;text-transform:none;font-weight:400;">
                    <input type="checkbox" id="rmPSendPdf" class="rm-checkbox" ${prompt.sendOriginalPdf ? 'checked' : ''}>
                    Send original PDF to Claude (default: send converted markdown — faster and cheaper)
                </label>
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px;">
                <button class="rm-btn rm-btn-danger" id="rmPDelete">Delete</button>
                <button class="rm-btn" id="rmPSave">Save</button>
                <button class="rm-btn rm-btn-primary" id="rmPSaveAdd">Save + Add to Project</button>
            </div>
        `;

        const saveBtn = form.querySelector('#rmPSave');
        const saveAddBtn = form.querySelector('#rmPSaveAdd');
        const deleteBtn = form.querySelector('#rmPDelete');
        saveBtn.addEventListener('click', () => withFeedback(saveBtn, () => this._save(prompt.id, form, false), { successText: '\u2713 Saved' }));
        saveAddBtn.addEventListener('click', () => withFeedback(saveAddBtn, () => this._save(prompt.id, form, true), { successText: '\u2713 Saved + Added' }));
        deleteBtn.addEventListener('click', () => withFeedback(deleteBtn, () => this._delete(prompt.id), { successText: '\u2713 Deleted' }));

        return form;
    }

    async _save(id, form, addToProject) {
        const scaleDescription = {};
        for (const input of form.querySelectorAll('.rm-scale-input')) {
            scaleDescription[input.dataset.range] = input.value.trim();
        }

        await this._promptStore.update(id, {
            name: form.querySelector('#rmPName').value.trim(),
            description: form.querySelector('#rmPDesc').value.trim(),
            promptText: form.querySelector('#rmPText').value.trim(),
            scaleDescription,
            sendOriginalPdf: form.querySelector('#rmPSendPdf').checked,
        });

        // Invalidate all existing scores for this criterion so they re-run
        this._onInvalidateScores(id);

        if (addToProject) {
            this._onAddToProject(id);
        }

        if (this._statusBar) {
            this._statusBar.log(
                addToProject ? 'Criterion saved, added to project, and scores invalidated' : 'Criterion saved — existing scores invalidated',
                'success');
        }
    }

    async _delete(id) {
        if (!confirm('Delete this evaluation criterion?')) return;
        await this._promptStore.remove(id);
        this._selectedId = null;
        if (this._statusBar) this._statusBar.log('Criterion deleted', 'info');
    }

    async _createNew() {
        const id = await this._promptStore.create({
            name: 'New Criterion',
            description: 'Describe what this evaluates',
            promptText: 'Evaluate this candidate\'s resume for [SPECIFIC QUALITY]. Consider their education, work experience, projects, and skills. Provide a score from 1-10.',
            scaleDescription: { ...DEFAULT_SCALE },
        }, this._userEmail);
        this._selectedId = id;
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }
}
